import { useFrame } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { type AgentState, useObserverStore } from "../../stores/observerStore";
import type { StarSystem } from "../../types/celestial";

const BASE_SCALE = 10;
const MARKER_COLOR = new THREE.Color("#2dd4a8");

/** Pulsing agent markers rendered as Points at system positions */
export function AgentMarkers(): React.JSX.Element | null {
	const agents = useObserverStore((s) => s.agents);
	const systems = useObserverStore((s) => s.systems);
	const pointsRef = useRef<THREE.Points>(null);

	// Build planet→system lookup
	const systemLookup = useMemo(() => {
		const map = new Map<string, StarSystem>();
		for (const sys of systems) {
			map.set(sys.id, sys);
			for (const p of sys.planets) {
				map.set(p.id, sys);
			}
		}
		return map;
	}, [systems]);

	// Group agents by system and compute positions
	const { positions, opacities, count } = useMemo(() => {
		const agentsBySystem = new Map<string, AgentState[]>();
		for (const agent of agents.values()) {
			const sys = systemLookup.get(agent.locationPlanetId);
			if (!sys) continue;
			const list = agentsBySystem.get(sys.id) ?? [];
			list.push(agent);
			agentsBySystem.set(sys.id, list);
		}

		const pos: number[] = [];
		const ops: number[] = [];
		for (const [sysId, agentList] of agentsBySystem) {
			const sys = systemLookup.get(sysId);
			if (!sys) continue;
			for (let i = 0; i < agentList.length; i++) {
				// Offset multiple agents slightly so they don't stack
				const offset = i * 4;
				pos.push(sys.x * BASE_SCALE + offset, -(sys.y * BASE_SCALE) + offset, 1);
				ops.push(1);
			}
		}

		return {
			positions: new Float32Array(pos),
			opacities: new Float32Array(ops),
			count: pos.length / 3,
		};
	}, [agents, systemLookup]);

	// Pulsing animation
	useFrame(({ clock }) => {
		if (!pointsRef.current || count === 0) return;
		const time = clock.getElapsedTime();
		const pulse = 0.6 + 0.4 * Math.sin(time * 3);
		const attr = pointsRef.current.geometry.getAttribute("opacity");
		if (attr) {
			for (let i = 0; i < count; i++) {
				(attr.array as Float32Array)[i] = pulse;
			}
			(attr as THREE.BufferAttribute).needsUpdate = true;
		}
	});

	if (count === 0) return null;

	const vertexShader = `
		attribute float opacity;
		varying float vOpacity;
		void main() {
			vOpacity = opacity;
			vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
			gl_PointSize = 8.0;
			gl_Position = projectionMatrix * mvPosition;
		}
	`;

	const fragmentShader = `
		varying float vOpacity;
		void main() {
			float dist = length(gl_PointCoord - vec2(0.5));
			if (dist > 0.5) discard;
			float alpha = smoothstep(0.5, 0.2, dist) * vOpacity;
			gl_FragColor = vec4(${MARKER_COLOR.r.toFixed(3)}, ${MARKER_COLOR.g.toFixed(3)}, ${MARKER_COLOR.b.toFixed(3)}, alpha);
		}
	`;

	return (
		<points ref={pointsRef} renderOrder={5}>
			<bufferGeometry>
				<bufferAttribute attach="attributes-position" args={[positions, 3]} />
				<bufferAttribute attach="attributes-opacity" args={[opacities, 1]} />
			</bufferGeometry>
			<shaderMaterial
				vertexShader={vertexShader}
				fragmentShader={fragmentShader}
				transparent
				depthWrite={false}
			/>
		</points>
	);
}
